import { adminApi, parseApiError } from './api';
import type { CostConfig, MarginAnalysis, CostSimulation, SimulationResult } from '@/types/admin.types';

export const costsService = {
  async getConfigs(): Promise<CostConfig[]> {
    try {
      const { data } = await adminApi.get<{ configs: CostConfig[] }>('/admin/costs/config');
      return data?.configs || [];
    } catch (error) {
      // Return empty array if endpoint doesn't exist yet
      const err = parseApiError(error);
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        return [];
      }
      throw err;
    }
  },

  async updateConfig(type: string, costPerUnit: number): Promise<CostConfig> {
    try {
      const { data } = await adminApi.put<CostConfig>(`/admin/costs/config/${type}`, { costPerUnit });
      return data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  async getMarginAnalysis(period?: string): Promise<MarginAnalysis> {
    try {
      const { data } = await adminApi.get<MarginAnalysis>('/admin/costs/margin', {
        params: period ? { period } : undefined,
      });
      return data || {
        totalRevenue: 0,
        totalCosts: 0,
        totalProfit: 0,
        avgMarginPercent: 0,
        byPlan: [],
      };
    } catch (error) {
      // Return default values if endpoint doesn't exist yet
      const err = parseApiError(error);
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        return {
          totalRevenue: 0,
          totalCosts: 0,
          totalProfit: 0,
          avgMarginPercent: 0,
          byPlan: [],
        };
      }
      throw err;
    }
  },

  async simulate(simulation: CostSimulation): Promise<SimulationResult> {
    try {
      const { data } = await adminApi.post<SimulationResult>('/admin/costs/simulate', simulation);
      return data || {
        estimatedCost: 0,
        planPrice: 0,
        estimatedProfit: 0,
        marginPercent: 0,
      };
    } catch (error) {
      throw parseApiError(error);
    }
  },
};

export default costsService;
